import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import CalendarView from '../components/screens/CalendarView';
import { useCalendar } from '../hooks/useCalendar';
import useFamilyPresence from '../hooks/useFamilyPresence';
import { toDateInputValue, toDateTimeLocalValue } from '../lib/dateTime';
import { createRecurrenceForm } from '../lib/recurrence';

function CalendarPage({ auth }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [scopeFilter, setScopeFilter] = useState('all');
  const [eventScope, setEventScope] = useState('personal');
  const [editingEventId, setEditingEventId] = useState('');
  const navigationCalendarParams = useMemo(
    () => ({
      focusEventId: new URLSearchParams(location.search).get('focusEventId') || '',
    }),
    [location.search]
  );
  const { hasFamily } = useFamilyPresence(auth.currentUser);

  const {
    calendarData,
    routineTasks,
    calendarParams,
    calendarStatus,
    eventForm,
    onCalendarFilterChange,
    refreshCalendar,
    onCreateEvent,
    onEventFormChange,
    onCalendarFiltersChange,
    onUpdateEvent,
    onDeleteEvent,
    completeRoutineTask,
  } = useCalendar(auth.currentUser);

  const resetEventEditor = () => {
    setEditingEventId('');
    setEventScope('personal');
    onEventFormChange('title', '');
    onEventFormChange('description', '');
    onEventFormChange('location', '');
    onEventFormChange('startAt', '');
    onEventFormChange('endAt', '');
    onEventFormChange('allDay', false);
    onEventFormChange('tags', '');
    onEventFormChange('recurrence', createRecurrenceForm());
  };

  const handleEditEvent = (calendarEvent) => {
    setEditingEventId(calendarEvent?.id || '');
    setEventScope(calendarEvent?.scope || 'personal');
    onEventFormChange('title', calendarEvent?.title || '');
    onEventFormChange('description', calendarEvent?.description || '');
    onEventFormChange('location', calendarEvent?.location || '');
    onEventFormChange('startAt', toDateTimeLocalValue(calendarEvent?.startAt));
    onEventFormChange('endAt', toDateTimeLocalValue(calendarEvent?.endAt));
    onEventFormChange('allDay', Boolean(calendarEvent?.allDay));
    onEventFormChange('tags', Array.isArray(calendarEvent?.tags) ? calendarEvent.tags.join(', ') : '');
    onEventFormChange('recurrence', createRecurrenceForm(calendarEvent?.recurrence));
  };

  const handleCreateEventSubmit = async (event) => {
    event.preventDefault();
    const payload = { ...eventForm, scope: eventScope };
    const succeeded = editingEventId
      ? await onUpdateEvent(editingEventId, payload)
      : await onCreateEvent(payload, { canUseFamilyScope: hasFamily });

    if (succeeded) {
      resetEventEditor();
    }
  };

  const handleDeleteEvent = async (eventId) => {
    const deleted = await onDeleteEvent(eventId);
    if (deleted && editingEventId === eventId) {
      resetEventEditor();
    }
  };

  // Client-side scope filter — all events already loaded from the unified endpoint
  const visibleEvents = scopeFilter === 'all'
    ? calendarData
    : calendarData.filter((e) => e.scope === scopeFilter);
  const visibleRoutineTasks = scopeFilter === 'personal'
    ? []
    : routineTasks;

  return (
    <CalendarView
      calendarStatus={calendarStatus}
      calendarParams={{ ...calendarParams, ...navigationCalendarParams }}
      calendarFilters={{
        ...calendarParams,
        ...navigationCalendarParams,
        scope: scopeFilter,
        startFrom: toDateInputValue(calendarParams.startFrom),
        startTo: toDateInputValue(calendarParams.startTo),
      }}
      eventForm={eventForm}
      eventScope={eventScope}
      editingEventId={editingEventId}
      canUseFamilyScope={hasFamily}
      calendarData={visibleEvents}
      routineTasks={visibleRoutineTasks}
      onCalendarFilterChange={(field, value) => {
        if (field === 'scope') {
          setScopeFilter(value);
        } else if (field === 'eventScope') {
          setEventScope(value);
        } else {
          onCalendarFilterChange(field, value);
        }
      }}
      onCalendarFiltersChange={onCalendarFiltersChange}
      onRefresh={refreshCalendar}
      onCompleteRoutine={completeRoutineTask}
      onOpenRoutine={(task) => {
        const taskId = task?.id || task?._id || task?.taskId;
        if (taskId) navigate(`/tasks?focusTaskId=${encodeURIComponent(taskId)}&view=routine`);
      }}
      onCreateEvent={handleCreateEventSubmit}
      onEventFormChange={onEventFormChange}
      onUpdateEvent={onUpdateEvent}
      onEditEvent={handleEditEvent}
      onDeleteEvent={handleDeleteEvent}
      onCancelEventEdit={resetEventEditor}
    />
  );
}

export default CalendarPage;
